import { format } from 'date-fns';
import { MonthYearString } from 'types/MonthYearString';

export const HIDDEN_TIME_UTC_HOURS = 0;
export const HIDDEN_TIME_UTC_MINUTES = 0;
export const HIDDEN_TIME_UTC_SECONDS = 0;

export const formatDateForDisplay = (date: string | Date | null, placeholder: string = '-'): string => {
  if (!date) return placeholder;
  if (date instanceof Date) {
    return date.toLocaleDateString([], { timeZone: 'UTC' });
  }
  return new Date(date).toLocaleDateString([], { timeZone: 'UTC' });
};

export const formatDateTimeForDisplay = (date: string, placeholder: string = '-'): string => {
  if (!date) return placeholder;
  if (formatHourForDisplay(date) === '-') {
    return `${formatDateForDisplay(date)}`;
  }
  return `${formatDateForDisplay(date)} - ${formatHourForDisplay(date)}`;
};

export const toDisplayHour = (date: string | Date): string => {
  const parsedDate = new Date(date);
  return parsedDate.toLocaleTimeString(['fr-FR'], { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
};

export const formatHourForDisplay = (date: string | null, placeholder: string = '-'): string => {
  if (!date) return placeholder;

  const parsedDate = new Date(date);
  // Hack! Magic hour (happy hour?) here.
  // If the user DID type 00h00, the second will be saved as 01.
  // If the user DID NOT select any hour, the time will be 00h00m00s.
  // See methods formatReceptionBeforePost, formatExpeditionBeforePost and formatProductionBeforePost from src/utils/normalizer.ts
  if (
    parsedDate.getUTCHours() === HIDDEN_TIME_UTC_HOURS &&
    parsedDate.getUTCMinutes() === HIDDEN_TIME_UTC_MINUTES &&
    parsedDate.getUTCSeconds() === HIDDEN_TIME_UTC_SECONDS
  )
    return placeholder;

  return parsedDate.toLocaleTimeString(['fr-FR'], { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
};

export const formatMonthForDisplay = (month: number, year: number): string => {
  const date = new Date(year, month - 1, 1); // we remove 1 because the month index starts at 0 in js

  return date.toLocaleString('default', { month: 'long' });
};

export const areDatesOnTheSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

export const checkDateIsLower = (
  dateToTest: Date | null | undefined,
  upperBoundary: Date | null | undefined,
): boolean => {
  // returns true if the dateToTest is either earlier than upperBoundary, either they are on the same date
  return (
    !!upperBoundary && !!dateToTest && (dateToTest <= upperBoundary || areDatesOnTheSameDay(dateToTest, upperBoundary))
  );
};

export const checkDateIsStrictlyLower = (
  dateToTest: Date | null | undefined,
  upperBoundary: Date | null | undefined,
): boolean => {
  // returns true if the dateToTest is either earlier than upperBoundary, either they are on the same date
  return (
    !!upperBoundary && !!dateToTest && dateToTest < upperBoundary && !areDatesOnTheSameDay(dateToTest, upperBoundary)
  );
};

export const checkDateUpperTimeInterval = (
  dateToTest: Date | null | undefined,
  upperBoundary: Date | null | undefined,
): boolean => {
  // returns true if the dateToTest is either earlier than upperBoundary, either they are on the same date
  return (
    !!upperBoundary && !!dateToTest && (dateToTest <= upperBoundary || areDatesOnTheSameDay(dateToTest, upperBoundary))
  );
};

export const checkDateLowerTimeInterval = (
  dateToTest: Date | null | undefined,
  lowerBoundary: Date | null | undefined,
): boolean => {
  return (
    !!lowerBoundary && !!dateToTest && dateToTest > lowerBoundary && !areDatesOnTheSameDay(dateToTest, lowerBoundary)
  );
};

export const getISOStringWithTimeZone = (date: string | Date): string => {
  const parsedDate = new Date(date);
  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');
  const hours = String(parsedDate.getHours()).padStart(2, '0');
  const minutes = String(parsedDate.getMinutes()).padStart(2, '0');
  const seconds = String(parsedDate.getSeconds()).padStart(2, '0');

  const offsetHours = -parsedDate.getTimezoneOffset() / 60;

  const stringOffset = (offsetHours > 0 ? '+' : '-') + String(offsetHours).padStart(2, '0') + ':00';

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000${stringOffset}`;
};

export const formatDateToMonthYear = (date: string | null, placeholder: string = '-'): string => {
  if (!date) return placeholder;

  return new Date(date).toLocaleDateString([], { year: 'numeric', month: 'long', timeZone: 'UTC' });
};

export const formatDateAsQueryParam = (date: Date, formatTime: boolean = false): string => {
  let dateFormat = 'y-MM-dd';
  if (formatTime) {
    dateFormat += ' HH:mm:ss.SSS';
  }
  return format(date, dateFormat);
};

export const createUTCDateFromDate = (date: Date) =>
  createUTCDate(
    date.getFullYear(),
    date.getMonth(),
    date.getDay(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
  );

export const createUTCDate = (
  year: number,
  month: number,
  day: number,
  hours: number,
  minutes: number,
  seconds: number,
) => new Date(Date.UTC(year, month, day, hours, minutes, seconds));

export const isValidDate = (date: Date | null | undefined) => {
  return date instanceof Date && !isNaN(date.getTime());
};

export function getMonthAndYearStringsBetweenTwoDates(
  startDate: Date | string | null,
  endDate: Date | null,
  asc: boolean = true,
): MonthYearString[] {
  const monthAndYearStringsBetweenTwoDates = [];

  if (!startDate || !endDate) {
    return [];
  }

  const currentDate = startDate instanceof Date ? startDate : new Date(startDate);

  currentDate.setDate(1);
  currentDate.setHours(0, 0, 0, 0);

  endDate.setDate(1);
  endDate.setHours(0, 0, 0, 0);

  while (currentDate <= endDate) {
    monthAndYearStringsBetweenTwoDates.push({
      monthInLetter: currentDate.toLocaleString('default', { month: 'long' }),
      month: currentDate.toLocaleString('default', { month: 'numeric' }),
      year: currentDate.toLocaleString('default', { year: 'numeric' }),
    });

    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return asc ? monthAndYearStringsBetweenTwoDates : monthAndYearStringsBetweenTwoDates.reverse();
}

export function addDaysToDate(date: Date, nDays: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + nDays);
}

export function atStartOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function atEndOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
}

export function createUTCDateFromDateString(date: string): Date {
  const currentDate = new Date(date);
  return new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate()));
}

export function createDatePlusTimezoneOffsetFromDateString(date: string): Date {
  const currentDate = new Date(date);
  const adjustedDateObj = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate(),
    currentDate.getHours(),
    currentDate.getMinutes(),
  );
  const timeZoneOffset = adjustedDateObj.getTimezoneOffset() * 60000;
  return new Date(adjustedDateObj.getTime() + timeZoneOffset);
}

export function getMonthDiff(date1: Date, date2: Date): number {
  const startDate = new Date(date1.getFullYear(), date1.getMonth() + 1, 0);
  const endDate = new Date(date2.getFullYear(), date2.getMonth() + 1, 0);
  return (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
}

export function getLatestDate(date1: Date | null, date2: Date | null): Date | null {
  if (date1 && date2) {
    return date1 > date2 ? date1 : date2;
  } else if (date1) {
    return date1;
  } else if (date2) {
    return date2;
  }
  return null;
}
